import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Loader2, ArrowRight } from "lucide-react";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const loggedInUser = await login(email, password);
      navigate(loggedInUser.must_change_password ? "/change-password" : "/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 md:p-12">
        <div className="w-full max-w-sm">
          <div className="mb-12">
            <h1
              className="text-4xl sm:text-5xl tracking-tighter font-medium text-slate-950"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              PayrollKE
            </h1>
            <p className="mt-2 text-sm text-slate-500">
              Kenyan payroll management system
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 block mb-2">
                Email
              </label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-white border border-slate-200 rounded-sm px-4 py-2.5 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm"
                placeholder="admin@example.com"
                required
              />
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 block mb-2">
                Password
              </label>
              <input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-white border border-slate-200 rounded-sm px-4 py-2.5 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm"
                placeholder="Enter password"
                required
              />
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="bg-[#FEF2F2] border border-[#EF4444] text-[#991B1B] text-sm px-4 py-3 rounded-sm"
              >
                {error}
              </div>
            )}

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="bg-[#002FA7] hover:bg-[#002480] text-white rounded-sm px-6 py-2.5 font-medium transition-colors focus:ring-2 focus:ring-[#002FA7]/30 w-full flex items-center justify-center gap-2 text-sm"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  Sign in <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate("/forgot-password")}
                className="text-sm text-[#002FA7] hover:text-[#002480] underline"
              >
                Forgot your password?
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Right - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1513694203232-719a280e022f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwYWJzdHJhY3QlMjBhcmNoaXRlY3R1cmUlMjB3aGl0ZSUyMGNsZWFufGVufDB8fHx8MTc3NjI1MjM3MHww&ixlib=rb-4.1.0&q=85"
          alt="Architecture"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-white/10" />
      </div>
    </div>
  );
}
